import { useState, useMemo, useEffect } from 'react';
import { useMytaverseEvent } from '@core/dashboard/providers';
import StreamService from '@services/StreamService';
import { getAverageValue } from '@core/devices/components/DevicesTest/ConnectionTest/helpers';
import { delay } from '@helpers/index';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';

export const useCheckServiceLatency = (enabled: boolean) => {
  const [serviceLatencies, setServiceLatencies] = useState<number[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);

  const { gameCastStreamRegion, currentEvent } = useMytaverseEvent();

  const averageServiceLatency = useMemo(
    () => getAverageValue(serviceLatencies),
    [serviceLatencies],
  );

  useEffect(() => {
    const region = gameCastStreamRegion || currentEvent?.region;

    if (!enabled || !region) {
      return;
    }

    setServiceLatencies([]);
    setIsCompleted(false);

    let cancelled = false;

    const setServiceLatencyValue = async () => {
      await delay(1000);

      try {
        const serviceLatency = await StreamService.checkServiceLatency(
          currentEvent?.eventRelease.gameliftEC2InstanceRegion || region,
        );

        if (cancelled) {
          return;
        }

        setServiceLatencies((prev) => prev.concat(serviceLatency));
      } catch (error) {
        MytaverseLogger.error(
          `Failed to check service latency: ${getCatchErrorMessage(error)}`,
          { eventId: currentEvent?.eventId },
        );
      }
    };

    const startCheckServiceLatency = async () => {
      const checkServiceLatencyList = [...new Array(5)];

      for (const [i] of checkServiceLatencyList.entries()) {
        if (cancelled) {
          return;
        }

        await setServiceLatencyValue();

        if (!cancelled && i === checkServiceLatencyList.length - 1) {
          setIsCompleted(true);
        }
      }
    };

    startCheckServiceLatency();

    return () => {
      cancelled = true;
    };
  }, [currentEvent, gameCastStreamRegion, enabled]);

  return {
    // null, not 0, when every probe failed — getAverageValue([]) would otherwise
    // be shown as a perfect 0 ms latency.
    serviceLatency: serviceLatencies.length ? averageServiceLatency : null,
    isCompleted,
  };
};
